import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { intersectionBy } from "lodash"
import { filterNotUndefinedOrNull } from "~/utils/tsUtils"
import { pickSourcingVendor } from "~/utils/sourcingVendor"

export function getCommonVendors(variants: IVariantSummary[]) {
  if (variants.length === 0) return []

  const allVendors = variants.map((sku) => [...sku.vendors])

  return intersectionBy(...allVendors, "id")
}

export const NO_VENDOR = "noVendor"

/**
 * Prefers preferredVendorId when it's a valid VendorOnSku entry — falls back to
 * vendors[0] when it's stale (no FK constraint, can point to a removed vendor).
 */
export function resolveVariantVendorId(
  variant: Pick<IVariantSummary, "settings" | "vendors">,
): string {
  return pickSourcingVendor(variant.vendors, variant.settings.preferredVendorId)?.id ?? NO_VENDOR
}

/**
 * Like resolveVariantVendorId, but also considers vendorManufactorName from the
 * plan's collection seasons. When all plan-scoped collections agree on one
 * vendorManufactorName AND that name matches a vendor in VendorOnSku, returns
 * that vendor's ID. Falls back to resolveVariantVendorId otherwise — no
 * behaviour change for customers without vendorManufactorName in extraData.
 */
export function resolveVariantVendorIdForPlan(
  variant: Pick<IVariantSummary, "settings" | "vendors" | "collections">,
  planCollectionNames: string[],
): string {
  if (planCollectionNames.length > 0) {
    const scopedCollections = variant.collections.filter((c) =>
      planCollectionNames.includes(c.name),
    )
    const vendorNames = new Set(
      filterNotUndefinedOrNull(
        scopedCollections.map((c) => c.extraData?.vendorManufactorName as string | undefined),
      ),
    )
    // Only act when all plan-scoped collections agree on one vendor name
    if (vendorNames.size === 1) {
      const vendorName = [...vendorNames][0]!
      const match = variant.vendors.find((v) => v.name.toLowerCase() === vendorName.toLowerCase())
      if (match) return match.id
    }
  }
  return resolveVariantVendorId(variant)
}
